/**
 * Chat Context Service
 * Fetches and formats user data to provide context to the AI chatbot
 */

import { getTreatments } from "../database/firestore";
import { getAllBirthDocuments } from "../database/planerFirestore";
import { getAllInseminations } from "../database/animalEvents";
import { getWorkItems } from "./workItemApi";
import { getUserSensors } from "../api/firestore/services/siloSensorService";
import { getAnimals } from "../api/firestore/services/animalService";
import { SUSPICIOUS_HEALTH_SCORE_THRESHOLD, isAnimalSuspicious } from "../utils/animalUtils";
import { getAllPredictions } from "../api/firestore/services/predictionService";
import { getAllNodes } from "../api/firestore/services/sensorService";
import {
        analyzeAnimals,
        analyzeTreatments,
        analyzePredictions,
        buildOperationalAlerts,
        analyzeDevices,
        analyzeSensors,
        analyzeProduction,
        analyzeWorkPlanner,
} from "./farmManager/contextAnalyzers";

/**
 * Fetches all relevant user context for the AI chatbot
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} Context object with formatted data summaries
 */
export async function fetchUserContext(userId) {
	try {
		const [animals, treatments, predictions, nodes, sensors, births, inseminations, workItems] = await Promise.all([
			getAnimals(userId).catch(() => []),
			getTreatments(userId).catch(() => []),
			getAllPredictions(userId, true).catch(() => []), // onlyCurrent = true
			getAllNodes(userId).catch(() => []),
			getUserSensors(userId).catch(() => []),
			getAllBirthDocuments(userId).catch(() => []),
			getAllInseminations(userId).catch(() => []),
			getWorkItems(userId, { limit: 50 }).catch(() => []),
		]);

                const insights = buildContextInsights(animals, treatments, predictions, nodes, sensors, births, inseminations, workItems);

                return {
                        animals: {
                                ...formatAnimalsContext(animals),
                                raw: animals,
                                list: animals,
                        },
                        treatments: {
                                ...formatTreatmentsContext(treatments),
                                raw: treatments,
                                list: treatments,
                        },
                        predictions: {
                                ...formatPredictionsContext(predictions),
                                raw: predictions,
                                list: predictions,
                        },
                        devices: {
                                ...formatDevicesContext(nodes),
                                raw: nodes,
                                list: nodes,
                        },
                        sensors: {
                                ...formatSensorsContext(sensors),
                                raw: sensors,
                                list: sensors,
                        },
                        production: {
                                ...formatProductionContext(births, inseminations),
                                births: births,
                                inseminations: inseminations,
                        },
                        workPlanner: {
                                ...formatWorkPlannerContext(workItems),
                                raw: workItems,
                                list: workItems,
                        },
                        insights,
                        summary: generateContextSummary(animals, treatments, predictions, insights, nodes, sensors, births, inseminations, workItems),
                };
	} catch (error) {
		console.error("[chatContextService][fetchUserContext] - Error fetching user context:", error);
        return {
                animals: {
                        count: 0,
                        suspicious: 0,
                        byGroup: {},
                        averageHealthScore: null,
                        suspiciousAnimals: [],
                        summary: "No animals found.",
                        raw: [],
                        list: [],
                },
                treatments: {
                        count: 0,
                        active: 0,
                        dueSoon: 0,
                        overdue: 0,
                        dueToday: 0,
                        summary: "No active treatments.",
                        raw: [],
                        list: [],
                },
                predictions: {
                        count: 0,
                        disease: 0,
                        birth: 0,
                        health: 0,
                        summary: "No active predictions.",
                        raw: [],
                        list: [],
                },
                devices: {
                        total: 0,
                        connected: 0,
                        disconnected: 0,
                        summary: "No devices found.",
                        raw: [],
                        list: [],
                },
                sensors: {
                        total: 0,
                        active: 0,
                        byType: {},
                        summary: "No sensors found.",
                        raw: [],
                        list: [],
                },
                production: {
                        recentBirths: 0,
                        recentWeanings: 0,
                        summary: "No recent production data.",
                },
                workPlanner: {
                        total: 0,
                        pending: 0,
                        inProgress: 0,
                        dueToday: 0,
                        summary: "No work items found.",
                        raw: [],
                        list: [],
                },
                insights: {
                        animalStats: analyzeAnimals([]),
                        treatmentStats: analyzeTreatments([]),
                        predictionStats: analyzePredictions([]),
                        alerts: [],
                        highlights: [],
                },
                summary: "Unable to load farm data at this time.",
        };
	}
}

/**
 * Formats device/BioNode data into a concise summary for AI context
 * @param {Array} nodes - Array of BioNode objects
 * @returns {Object} Formatted device context
 */
function formatDevicesContext(nodes) {
        if (!nodes || nodes.length === 0) {
                return {
                        total: 0,
                        connected: 0,
                        disconnected: 0,
                        summary: "No devices found.",
                };
        }

        const stats = analyzeDevices(nodes);
        return {
                total: stats.total,
                connected: stats.connected,
                disconnected: stats.disconnected,
                summary: `${stats.total} BioNodes: ${stats.connected} connected, ${stats.disconnected} disconnected.`,
        };
}

/**
 * Formats sensor data into a concise summary for AI context
 * @param {Array} sensors - Array of sensor objects
 * @returns {Object} Formatted sensor context
 */
function formatSensorsContext(sensors) {
        if (!sensors || sensors.length === 0) {
                return {
                        total: 0,
                        active: 0,
                        byType: {},
                        summary: "No sensors found.",
                };
        }

        const stats = analyzeSensors(sensors);
        return {
                total: stats.total,
                active: stats.active,
                byType: stats.byType,
                summary: `${stats.total} sensors (${stats.active} active): ${Object.entries(stats.byType).map(([type, count]) => `${type}: ${count}`).join(", ")}.`,
        };
}

/**
 * Formats production data (calvings and weaning records) into a concise summary for AI context
 * @param {Array} births - Array of birth/calving documents
 * @param {Array} weaningRecords - Array of weaning documents
 * @returns {Object} Formatted production context
 */
function formatProductionContext(births, weaningRecords) {
        const stats = analyzeProduction(births, weaningRecords);
        return {
                recentBirths: stats.recentBirths,
                recentWeanings: stats.recentWeanings,
                summary: `Recent production: ${stats.recentBirths} calvings, ${stats.recentWeanings} weanings in the last 30 days.`,
        };
}

/**
 * Formats work planner data into a concise summary for AI context
 * @param {Array} workItems - Array of work item objects
 * @returns {Object} Formatted work planner context
 */
function formatWorkPlannerContext(workItems) {
        if (!workItems || workItems.length === 0) {
                return {
                        total: 0,
                        pending: 0,
                        inProgress: 0,
                        dueToday: 0,
                        summary: "No work items found.",
                };
        }

        const stats = analyzeWorkPlanner(workItems);
        return {
                total: stats.total,
                pending: stats.pending,
                inProgress: stats.inProgress,
                dueToday: stats.dueToday,
                summary: `${stats.total} work items: ${stats.pending} pending, ${stats.inProgress} in progress, ${stats.dueToday} due today.`,
        };
}

/**
 * Formats animal data into a concise summary for AI context
 * @param {Array} animals - Array of animal objects
 * @returns {Object} Formatted animal context
 */
function formatAnimalsContext(animals) {
        if (!animals || animals.length === 0) {
                return {
                        count: 0,
                        suspicious: 0,
                        byGroup: {},
                        averageHealthScore: null,
                        suspiciousAnimals: [],
                        summary: "No animals found.",
                };
        }

        const stats = analyzeAnimals(animals);
        const byGroup = {};
        stats.groupSpread.forEach((count, group) => {
                byGroup[group] = count;
        });

        const suspiciousAnimals = stats.suspicious.slice(0, 5).map((animal) => ({
                id: animal.id,
                mac: animal.mac,
                healthScore: animal.healthScore,
                group: animal.group,
                location: animal.location,
        }));

        return {
                count: stats.total,
                suspicious: stats.suspiciousCount,
                byGroup,
                averageHealthScore: stats.averageHealthScore,
                suspiciousAnimals,
                summary: `${stats.total} active animals. ${stats.suspiciousCount} need attention.`,
        };
}

/**
 * Formats treatment data into a concise summary for AI context
 * @param {Array} treatments - Array of treatment objects
 * @returns {Object} Formatted treatment context
 */
function formatTreatmentsContext(treatments) {
        if (!treatments || treatments.length === 0) {
                return {
                        count: 0,
                        active: 0,
                        dueSoon: 0,
                        summary: "No active treatments.",
                };
        }

        const stats = analyzeTreatments(treatments);
        return {
                count: stats.total,
                active: stats.active.length,
                dueSoon: stats.overdue.length + stats.dueToday.length,
                overdue: stats.overdue.length,
                dueToday: stats.dueToday.length,
                summary: `${stats.active.length} active treatments. ${stats.overdue.length} overdue, ${stats.dueToday.length} scheduled for today.`,
        };
}

/**
 * Formats prediction data into a concise summary for AI context
 * @param {Array} predictions - Array of prediction objects
 * @returns {Object} Formatted prediction context
 */
function formatPredictionsContext(predictions) {
        if (!predictions || predictions.length === 0) {
                return {
                        count: 0,
                        disease: 0,
                        birth: 0,
                        health: 0,
                        summary: "No active predictions.",
                };
        }

        const stats = analyzePredictions(predictions);

        return {
                count: stats.total,
                disease: stats.disease.length,
                birth: stats.birth.length,
                health: stats.health.length,
                summary: `${stats.total} active predictions: ${stats.disease.length} disease, ${stats.birth.length} birth, ${stats.health.length} health.`,
        };
}

/**
 * Generates a comprehensive context summary for the AI system prompt
 * @param {Array} animals - Array of animal objects
 * @param {Array} treatments - Array of treatment objects
 * @param {Array} predictions - Array of prediction objects
 * @returns {string} Context summary string
 */
function buildContextInsights(animals, treatments, predictions, nodes = [], sensors = [], births = [], inseminations = [], workItems = []) {
        const animalStats = analyzeAnimals(animals);
        const treatmentStats = analyzeTreatments(treatments);
        const predictionStats = analyzePredictions(predictions);
        const alerts = buildOperationalAlerts({ animalStats, treatmentStats, predictionStats });

        const highlights = [];

        if (typeof animalStats.averageHealthScore === "number") {
                highlights.push(`Average herd health score ${animalStats.averageHealthScore}.`);
        }

        if (alerts.length > 0) {
                highlights.push(alerts.map((alert) => alert.message).join(" "));
        }

        if (treatmentStats.dueToday.length > 0) {
                highlights.push(`${treatmentStats.dueToday.length} treatments scheduled for today.`);
        }

        if (predictionStats.birth.length > 0) {
                highlights.push(`${predictionStats.birth.length} birth predictions in queue.`);
        }

        return {
                animalStats,
                treatmentStats,
                predictionStats,
                alerts,
                highlights,
        };
}

function generateContextSummary(animals, treatments, predictions, insights, nodes = [], sensors = [], births = [], inseminations = [], workItems = []) {
        const animalSummary = formatAnimalsContext(animals);
        const treatmentSummary = formatTreatmentsContext(treatments);
        const predictionSummary = formatPredictionsContext(predictions);
        const deviceSummary = formatDevicesContext(nodes);
        const sensorSummary = formatSensorsContext(sensors);
        const productionSummary = formatProductionContext(births, inseminations);
        const workPlannerSummary = formatWorkPlannerContext(workItems);

        const alertSummary = insights?.alerts?.length
                ? ` Alerts: ${insights.alerts.map((alert) => alert.message).join("; ")}.`
                : "";

        const highlightSummary = insights?.highlights?.length ? ` ${insights.highlights.join(" ")}` : "";

        const baseSummary = `Farm Overview: ${animalSummary.summary} ${treatmentSummary.summary} ${predictionSummary.summary}. ${deviceSummary.summary} ${sensorSummary.summary} ${productionSummary.summary} ${workPlannerSummary.summary}`;

        return `${baseSummary}${alertSummary}${highlightSummary}`.trim();
}

/**
 * Fetches all suspicious animals that need attention
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of suspicious animal objects with ID, HealthScore, Group, and Location
 */
export async function getSuspiciousAnimals(userId) {
	try {
		const animals = await getAnimals(userId);
		
		// Filter for suspicious animals (HealthScore below threshold; legacy flags as fallback)
		const suspicious = animals.filter((a) => isAnimalSuspicious(a));

		// Return simplified objects with essential info
		return suspicious.map(animal => ({
			ID: animal.ID,
			MAC: animal.MAC,
			HealthScore: animal.HealthScore,
			Group: animal.Group || "-",
			Location: animal.Location || "-",
			Suspicious: animal.Suspicious || animal.Sus || false,
		}));
	} catch (error) {
		console.error("[chatContextService][getSuspiciousAnimals] - Error fetching suspicious animals:", error);
		return [];
	}
}

/**
 * Builds proactive context suggestions based on current farm state and time
 *
 * This function analyzes the farm context and current time to generate
 * suggestions that the AI can proactively mention to the user.
 *
 * @param {Object} insights - Context insights from buildContextInsights
 * @param {Date} currentTime - Current time (defaults to now)
 * @returns {Array} Array of proactive suggestion objects
 */
export function buildProactiveContext(insights, currentTime = new Date()) {
	const suggestions = [];

	if (!insights) {
		return suggestions;
	}

	const hour = currentTime.getHours();
	const dayOfWeek = currentTime.getDay(); // 0 = Sunday, 1 = Monday, etc.

	const { animalStats, treatmentStats, predictionStats } = insights;

	// ==================== TIME-BASED SUGGESTIONS ====================

	// Morning check context (6-10 AM on weekdays)
	if (hour >= 6 && hour < 10 && dayOfWeek >= 1 && dayOfWeek <= 5) {
		// Treatment reminders
		if (treatmentStats?.dueToday?.length > 0) {
			suggestions.push({
				type: "reminder",
				priority: "high",
				category: "treatments",
				message: `${treatmentStats.dueToday.length} treatments scheduled for today`,
				action: "Consider reviewing the treatment schedule",
				toolSuggestion: "work-planner-overview",
			});
		}

		// Birth/calving predictions - important for morning preparation
		if (predictionStats?.birth?.length > 0) {
			const urgentBirths = predictionStats.birth.filter((p) => {
				// Check if predicted within next 24-48 hours
				const predDate = p.PredictedDate || p.predictedDate;
				if (!predDate) return false;
				const predictedTime = new Date(predDate).getTime();
				const hoursUntil = (predictedTime - currentTime.getTime()) / (1000 * 60 * 60);
				return hoursUntil >= 0 && hoursUntil <= 48;
			});

			if (urgentBirths.length > 0) {
				suggestions.push({
					type: "alert",
					priority: "high",
					category: "calving",
					message: `${urgentBirths.length} animals with calving predictions in the next 48 hours`,
					action: "Check calving preparations",
					toolSuggestion: "birth-predictions",
				});
			}
		}

		// Vaccination reminders
		if (treatmentStats?.upcoming?.length > 0) {
			suggestions.push({
				type: "reminder",
				priority: "medium",
				category: "vaccination",
				message: `${treatmentStats.upcoming.length} upcoming vaccinations in the next 3 days`,
				action: "Review vaccination schedule",
				toolSuggestion: "vaccination-status",
			});
		}
	}

	// Weekly review context (Monday mornings 7-12)
	if (dayOfWeek === 1 && hour >= 7 && hour < 12) {
		suggestions.push({
			type: "scheduled",
			priority: "medium",
			category: "reporting",
			message: "Weekly performance review opportunity",
			action: "Would you like me to generate a weekly summary report?",
			toolSuggestion: "generate-report",
		});
	}

	// End of day check (4-6 PM on weekdays)
	if (hour >= 16 && hour < 18 && dayOfWeek >= 1 && dayOfWeek <= 5) {
		// Overdue treatments reminder
		if (treatmentStats?.overdue?.length > 0) {
			suggestions.push({
				type: "reminder",
				priority: "high",
				category: "treatments",
				message: `${treatmentStats.overdue.length} overdue treatments need attention before end of day`,
				action: "Review overdue treatments",
				toolSuggestion: "treatment-history",
			});
		}
	}

	// ==================== HEALTH-BASED SUGGESTIONS ====================

	// Urgent health context - multiple suspicious animals
	if (animalStats?.suspiciousCount > 5) {
		suggestions.push({
			type: "urgent",
			priority: "high",
			category: "health",
			message: `${animalStats.suspiciousCount} animals require attention (health score < ${SUSPICIOUS_HEALTH_SCORE_THRESHOLD})`,
			action: "Review suspicious animals immediately",
			toolSuggestion: "suspicious-animals",
		});
	}

	// Critical animals (very low health scores)
	if (animalStats?.suspicious?.length > 0) {
		const criticalAnimals = animalStats.suspicious.filter(
			(a) => typeof a.healthScore === "number" && a.healthScore < 30
		);
		if (criticalAnimals.length > 0) {
			suggestions.push({
				type: "critical",
				priority: "critical",
				category: "health",
				message: `${criticalAnimals.length} animals have critical health scores (< 30)`,
				action: "Immediate veterinary consultation recommended",
				toolSuggestion: "health-diagnosis-assist",
			});
		}
	}

	// Low average herd health
	if (typeof animalStats?.averageHealthScore === "number" && animalStats.averageHealthScore < 60) {
		suggestions.push({
			type: "alert",
			priority: "medium",
			category: "health",
			message: `Herd average health score is ${animalStats.averageHealthScore.toFixed(1)}% (below optimal)`,
			action: "Consider investigating environmental or health factors",
			toolSuggestion: "activity-anomaly-analysis",
		});
	}

	// ==================== DISEASE-BASED SUGGESTIONS ====================

	// Disease overview - calves with active disease predictions
	if (predictionStats?.disease?.length > 0) {
		suggestions.push({
			type: "alert",
			priority: "high",
			category: "disease",
			message: `${predictionStats.disease.length} calves with active disease predictions`,
			action: "Review disease overview and health scoring",
			toolSuggestion: "disease-overview",
		});
	}

	// Sort suggestions by priority
	const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
	suggestions.sort((a, b) => (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3));

	return suggestions;
}

/**
 * Formats proactive suggestions for inclusion in the system prompt
 * @param {Array} suggestions - Array of suggestion objects
 * @param {string} language - Language code (en, de, etc.)
 * @returns {string} Formatted suggestions string
 */
export function formatProactiveContextForPrompt(suggestions, language = "en") {
	if (!suggestions || suggestions.length === 0) {
		return "";
	}

	const isGerman = language.startsWith("de") || language === "ger";
	const parts = [];

	parts.push(isGerman ? "PROAKTIVE HINWEISE (bei Gelegenheit erwähnen):" : "PROACTIVE SUGGESTIONS (mention when relevant):");

	suggestions.slice(0, 5).forEach((suggestion) => {
		const priorityLabel = {
			critical: isGerman ? "KRITISCH" : "CRITICAL",
			high: isGerman ? "HOCH" : "HIGH",
			medium: isGerman ? "MITTEL" : "MEDIUM",
			low: isGerman ? "NIEDRIG" : "LOW",
		}[suggestion.priority] || "";

		parts.push(`- [${priorityLabel}] ${suggestion.message}`);
		if (suggestion.action) {
			parts.push(`  → ${suggestion.action}`);
		}
	});

	return parts.join("\n");
}

